import { UserStats } from '../types/user.entity';

export const formatDuration = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0m';
  
  if (minutes < 60) return `${Math.round(minutes)}m`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

export const formatCurrency = (amount: number): string => {
  if (!amount || amount <= 0) return '0.00';
  
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export const formatPercentage = (value: number, total: number): string => {
  if (!total || total <= 0) return '0%';
  
  const percentage = (value / total) * 100;
  return `${Math.round(percentage)}%`;
};

export const validateUserStats = (stats: UserStats | null): boolean => {
  if (!stats) return false;
  
  // Check if at least one category has meaningful data
  const hasBookingStats = stats.bookingStats && Object.values(stats.bookingStats).some(val => val > 0);
  const hasKarma = stats.karma && Object.values(stats.karma).some(val => val > 0);
  const hasWithdrawals = stats.withdrawalInvoicesCounters && Object.keys(stats.withdrawalInvoicesCounters).length > 0;
  const hasLearningDuration = stats.learningDurationMinutes && Object.values(stats.learningDurationMinutes).some(val => val > 0);
  const hasCoachingDuration = stats.coachingDurationMinutes && Object.values(stats.coachingDurationMinutes).some(val => val > 0);
  
  return hasBookingStats || hasKarma || hasWithdrawals || hasLearningDuration || hasCoachingDuration;
};

export const getTotalBookings = (stats: UserStats | null): number => {
  if (!stats?.bookingStats) return 0;
  
  return (
    (stats.bookingStats.awaitingCount ?? 0) +
    (stats.bookingStats.upcomingCount ?? 0) +
    (stats.bookingStats.completedCount ?? 0) +
    (stats.bookingStats.unattendedCount ?? 0)
  );
};

export const getTotalLearningTime = (stats: UserStats | null): number => {
  if (!stats?.learningDurationMinutes) return 0;
  
  return (
    (stats.learningDurationMinutes.directCall ?? 0) +
    (stats.learningDurationMinutes.scheduled ?? 0) +
    (stats.learningDurationMinutes.ai ?? 0)
  );
};

export const getTotalCoachingTime = (stats: UserStats | null): number => {
  if (!stats?.coachingDurationMinutes) return 0;
  
  return (
    (stats.coachingDurationMinutes.directCall ?? 0) +
    (stats.coachingDurationMinutes.scheduled ?? 0)
  );
};

export const prepareChartData = (stats: UserStats | null) => {
  if (!stats) return null;
  
  return {
    bookingData: [
      { name: 'Awaiting', value: Math.round(stats.bookingStats?.awaitingCount ?? 0), color: '#16a34a' },
      { name: 'Upcoming', value: Math.round(stats.bookingStats?.upcomingCount ?? 0), color: '#ea580c' },
      { name: 'Completed', value: Math.round(stats.bookingStats?.completedCount ?? 0), color: '#1d4ed8' },
      { name: 'Unattended', value: Math.round(stats.bookingStats?.unattendedCount ?? 0), color: '#b91c1c' }
    ],
    withdrawalData: stats.withdrawalInvoicesCounters 
      ? Object.entries(stats.withdrawalInvoicesCounters)
          .map(([year, count]) => ({ year, count: Math.round(count) }))
          .sort((a, b) => parseInt(a.year) - parseInt(b.year))
      : [],
    learningDurationData: [
      { name: 'Direct Call', value: Math.round(stats.learningDurationMinutes?.directCall ?? 0), color: '#10b981' },
      { name: 'Scheduled', value: Math.round(stats.learningDurationMinutes?.scheduled ?? 0), color: '#3b82f6' },
      { name: 'AI', value: Math.round(stats.learningDurationMinutes?.ai ?? 0), color: '#f59e0b' }
    ],
    coachingDurationData: [
      { name: 'Direct Call', value: Math.round(stats.coachingDurationMinutes?.directCall ?? 0), color: '#10b981' },
      { name: 'Scheduled', value: Math.round(stats.coachingDurationMinutes?.scheduled ?? 0), color: '#3b82f6' }
    ]
  };
};
